/**
 * Выбор источника показателя (US-08, US-39).
 *
 * С US-39 (DP-545) источник — свойство «показатель × территория × год», и
 * выбирается он из трёх значений: **БНС · Мониторинг СНП · Ручной ввод** (у
 * бюджета — **ИС «Казначейство-клиент» · Ручной ввод**). Какие варианты есть у
 * показателя, что выбрано и закрыто ли поле — всё это решает сервер и присылает
 * в ответе формы (`source_options`, `source_mode`, `locked`). Экран ничего не
 * додумывает: ввод открыт только на «Ручном вводе», на любом источнике поле
 * заблокировано — даже если источник значения не дал (FR-11, FR-11.1).
 *
 * **Выбор хранит сервер** — таблица `source_mode`, ключ «показатель × КАТО ×
 * год × регион»; меняет его `setSourceMode`, отдельным запросом, не дожидаясь
 * «Сохранить». Раньше он лежал в `localStorage` браузера, и переключение по
 * одному району переключало показатель во всех территориях региона.
 */
import type {
  PassportBlock,
  PassportField,
  PassportForm,
  SourceOption,
} from '../../../requests/input_data.request';

export type { SourceMode } from '../../../requests/input_data.request';

/** Значение тумблера «Ручной ввод» — единственное, на котором поле открыто. */
export const MANUAL = 'manual';

/** Прежнее двоичное «оставить источник» — только у бэкенда до US-39. */
const LEGACY_SOURCE = 'source';

export interface SourceState {
  /** Варианты тумблера в порядке показа; пусто — тумблера нет, поле открыто. */
  options: SourceOption[];
  /** Выбор с сервера; `null` — источника нет. */
  mode: string | null;
  /** Поле заблокировано при этом выборе — как решил сервер. */
  locked: boolean;
}

/** Бэкенд до US-39: полей выбора нет вовсе — см. `sourceStateOf`. */
const isLegacy = (entry: PassportField | PassportBlock): boolean =>
  entry.source_options === undefined;

/** Выбор источника элемента формы, как его прислал сервер; `null` — источника нет. */
export const modeOf = (entry: PassportField | PassportBlock): string | null => {
  if (!isLegacy(entry)) return entry.source_mode ?? null;
  if (!entry.snp_source) return null;
  return entry.manual_mode ? MANUAL : LEGACY_SOURCE;
};

/**
 * Состояние источника элемента формы.
 *
 * Бэкенд до US-39 полей выбора не присылает вовсе — тогда собираем прежний
 * двоичный тумблер «Мониторинг СНП / Ручной ввод» по `snp_source` и
 * `manual_mode`. Новый бэкенд шлёт `source_options` каждому элементу, в том
 * числе пустым списком, — и до фолбэка дело не доходит.
 */
export const sourceStateOf = (
  entry: PassportField | PassportBlock,
  legacyLabels: { snp: SourceOption; manual: SourceOption },
): SourceState => {
  const mode = modeOf(entry);
  if (!isLegacy(entry)) {
    return { options: entry.source_options ?? [], mode, locked: entry.locked ?? false };
  }
  if (mode === null) return { options: [], mode, locked: false };
  return {
    options: [legacyLabels.snp, legacyLabels.manual],
    mode,
    locked: mode !== MANUAL,
  };
};

/** Подпись варианта «Мониторинг СНП» для фолбэка — значение прежнего API. */
export const legacySnpOption = (labelRu: string, labelKz: string): SourceOption => ({
  value: LEGACY_SOURCE,
  label_ru: labelRu,
  label_kz: labelKz,
});

/**
 * Выбор по каждому элементу формы — ключ `field.key` либо `block.id`, как их
 * прислал сервер и как их принимает `setSourceMode`.
 */
export const modesOf = (form: PassportForm): Record<string, string | null> => {
  const modes: Record<string, string | null> = {};
  for (const section of form.sections) {
    for (const field of section.fields) modes[field.key] = modeOf(field);
    for (const block of section.blocks) modes[block.id] = modeOf(block);
  }
  return modes;
};

/**
 * Закрыто ли поле при текущем выборе.
 *
 * Пока выбор совпадает с присланным — верим серверу (`locked`). Оператор
 * переключил тумблер, а ответ с новым состоянием ещё не пришёл, — правило
 * FR-11: открыт только «Ручной ввод».
 */
export const isLocked = (state: SourceState, current: string | null): boolean => {
  if (current === state.mode) return state.locked;
  return current !== null && current !== MANUAL;
};
